import { FieldsContext } from '@/contexts/FieldsContext'
import { useSheet } from '@/contexts/SheetContext'
import { getPendingRules } from '@/contexts/RulesContext'
import { useSheetCounts } from '@/hooks/sheet/useSheetCounts'
import {
  Button,
  FieldFilterDropdown,
  FilterGroup,
  FilterValidationTabs,
  Icon,
  Text,
} from '@flatfile/baseline'
import { memo, useCallback, useContext, useMemo } from 'react'
import { useFilterSheet } from '../sheet/store/hooks/useFilterSheet'
import { EnrichmentStatusIndicator } from './EnrichmentStatusIndicator'
import SheetFilterArea from './filter-area'

interface SheetToolbarProps {
  readonly sheetId: string
  readonly commitCurrentChanges: (columnConfig: any) => void
  readonly isSidebarCollapsed?: boolean
  readonly handleSidebarCollapse?: (collapsed: boolean) => void
  setShowEnrichment?: (show: boolean) => void
  showEnrichment?: boolean
  onEnrichmentEdit?: (enrichmentKey: string) => void
  setShowRules?: (show: boolean) => void
  showRules?: boolean
  onRuleEdit?: (ruleId: string) => void
}

// Safely access counts without triggering re-renders during render phase
const SafeFilterValidationTabs = memo(({ counts }: { counts?: any }) => {
  return <FilterValidationTabs filteredCounts={counts} />
})

SafeFilterValidationTabs.displayName = 'SafeFilterValidationTabs'

export const Toolbar = memo(function Toolbar({
  sheetId,
  commitCurrentChanges,
  isSidebarCollapsed,
  handleSidebarCollapse,
  setShowEnrichment,
  showEnrichment,
  onEnrichmentEdit,
  setShowRules,
  showRules,
  onRuleEdit,
}: SheetToolbarProps) {
  const { sheet } = useSheet()
  const pendingRules = getPendingRules(sheet)
  // Deduplicate by toolId
  const { columnConfig } = useContext(FieldsContext)
  const {
    queryCollections,
    setActiveQuery,
    createCollection,
    setActiveQueryCollection,
  } = useFilterSheet(sheetId)

  // Memoize computed values to prevent unnecessary calculations during render
  const completeQueriesCount = useMemo(
    () =>
      Object.values(queryCollections || {}).filter(
        (collection) => collection?.queries?.length
      ).length,
    [queryCollections]
  )

  // Use try-catch to safeguard against potential errors during render
  const { sheetCounts } = useSheetCounts({ sheetId }) || { sheetCounts: null }

  const fieldGroup = useMemo(
    () => ({
      label: 'All Fields',
      options: columnConfig.map((column) => ({
        label: column?.label ?? column?.value,
        key: column.value,
      })),
    }),
    [columnConfig]
  )

  const fieldOptionGroups = useMemo(() => [fieldGroup], [fieldGroup])

  const handleFieldSelected = useCallback(
    (field: string) => {
      const newQuery = {
        queryField: field,
        queryOperation: 'eq',
        queryValues: [],
      }
      const createdCollection = createCollection(newQuery, 'atomic')
      setActiveQuery(createdCollection.queries[0].id)
      setActiveQueryCollection(createdCollection.id)
    },
    [createCollection, setActiveQuery, setActiveQueryCollection]
  )

  const handleAdvancedFilter = useCallback(() => {
    const createdCollection = createCollection(undefined, 'advanced')
    setActiveQueryCollection(createdCollection.id)
  }, [createCollection, setActiveQueryCollection])

  const advancedFilterAction = useMemo(
    () => ({
      label: 'Advanced filter',
      icon: 'settings-slider-three',
      onClick: handleAdvancedFilter,
    }),
    [handleAdvancedFilter]
  )

  return (
    <div className='w-full flex flex-col items-center gap-2 bg-background-80 px-2 pt-[11.5px] pb-[9.5px]'>
      <div className='flex items-center gap-2 w-full'>
        <div
          className={`
            flex items-center justify-center
            overflow-hidden
            transition-[max-width,opacity] duration-300 ease-in-out
            ${isSidebarCollapsed ? 'max-w-10 opacity-100' : 'max-w-0 opacity-0'}
          `}
        >
          <Button
            variant='ghost'
            size='icon-md'
            onClick={() =>
              handleSidebarCollapse &&
              handleSidebarCollapse(!isSidebarCollapsed)
            }
            icon='sidebar-right-arrow-thin'
            className={`flex-shrink-0 ${
              !isSidebarCollapsed ? 'pointer-events-none' : ''
            }`}
          />
        </div>
        <div className='w-full flex justify-between items-center'>
          <FieldFilterDropdown
            onFieldSelected={handleFieldSelected}
            options={fieldOptionGroups as FilterGroup[]}
            footerActions={[advancedFilterAction]}
          >
            <Button variant='ghost' size='sm' className='relative'>
              <Icon name='filter-2' />
              <Text color='muted-foreground'>Filter</Text>
              {completeQueriesCount > 0 && (
                <div className='flex h-4 w-4 -ml-1 items-center justify-center rounded-full bg-foreground'>
                  <Text color='base-white' className='text-xs text-base-white'>
                    {completeQueriesCount}
                  </Text>
                </div>
              )}
            </Button>
          </FieldFilterDropdown>
          <SheetFilterArea
            sheetId={sheetId}
            onFieldSelected={handleFieldSelected}
            fieldOptionGroups={fieldOptionGroups}
          />
          <div className='flex items-center gap-2'>
            <div className='flex items-center gap-2 pr-2'>
              <div className='flex items-center gap-1'>
                <Button
                  variant='primary'
                  size='sm'
                  className='relative'
                  disabled={showEnrichment || showRules}
                  onClick={() => setShowEnrichment?.(true)}
                >
                  <Icon name='lightning' />
                  <Text color='white'>Add Enrichment</Text>
                </Button>
                <EnrichmentStatusIndicator
                  sheetId={sheetId}
                  onEnrichmentClick={(enrichmentKey) => {
                    // Open enrichment panel for editing
                    if (onEnrichmentEdit) {
                      onEnrichmentEdit(enrichmentKey)
                    }
                  }}
                />
                <Button
                  variant='outline'
                  size='sm'
                  className='relative'
                  disabled={showEnrichment || showRules}
                  onClick={() => setShowRules?.(true)}
                >
                  <Icon name='book' />
                  <Text>Rules ({pendingRules.length})</Text>
                </Button>
              </div>
              <SafeFilterValidationTabs counts={sheetCounts?.counts} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})
